import { useCallback } from 'react'
import { useReactFlow, type Node } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../../../types/mindmap-components'
import { Z_INDEX } from '../../../../styles/theme'

/**
 * Optimized z-index update that only modifies nodes that actually need changes.
 * Returns the same array reference if no changes are needed.
 */
function updateZIndexOptimized(
  nodes: Node<ReactFlowNodeData>[],
  targetNodeId: string
): Node<ReactFlowNodeData>[] {
  // Find target node and check if update is needed
  const targetNode = nodes.find((n) => n.id === targetNodeId)
  if (!targetNode) {
    return nodes
  }

  // Check if target already has correct z-index
  const targetAlreadyFront = targetNode.zIndex === Z_INDEX.reactFlowNodeFocused

  // Check if any other nodes need z-index reset
  const nodesToReset = nodes.filter(
    (n) =>
      n.id !== targetNodeId && n.zIndex !== undefined && n.zIndex >= Z_INDEX.reactFlowNodeThreshold
  )

  // If no changes needed, return same array reference
  if (targetAlreadyFront && nodesToReset.length === 0) {
    return nodes
  }

  // Build set of node IDs that need updates for O(1) lookup
  const nodeIdsToUpdate = new Set<string>()
  if (!targetAlreadyFront) {
    nodeIdsToUpdate.add(targetNodeId)
  }
  nodesToReset.forEach((n) => nodeIdsToUpdate.add(n.id))

  // Only create new objects for nodes that actually change
  return nodes.map((node) => {
    if (!nodeIdsToUpdate.has(node.id)) {
      return node // Return same reference - no change
    }

    if (node.id === targetNodeId) {
      return { ...node, zIndex: Z_INDEX.reactFlowNodeFocused }
    } else {
      return { ...node, zIndex: undefined }
    }
  })
}

/**
 * Custom hook for managing node z-index to bring nodes to the front
 * Centralizes all z-index logic to avoid code duplication
 *
 * @param nodeId - The ID of the node to manage z-index for
 * @returns Function to bring the node to the front
 */
export function useNodeZIndex(nodeId: string) {
  const { setNodes } = useReactFlow<Node<ReactFlowNodeData>>()

  /**
   * Brings this node to the front by setting its zIndex to Z_INDEX.reactFlowNodeFocused
   * and resetting all other nodes' zIndex to undefined.
   * Optimized to only update nodes that actually need changes.
   */
  const bringNodeToFront = useCallback(() => {
    setNodes((nodes) => updateZIndexOptimized(nodes, nodeId))
  }, [nodeId, setNodes])

  return bringNodeToFront
}

/**
 * Utility function to bring a node to the front from outside a component
 * Useful for drag handlers in MindMapFlow
 *
 * @param nodeId - The ID of the node to bring to front
 * @param setNodes - React Flow's setNodes function
 */
export function bringNodeToFront(
  nodeId: string,
  setNodes: (
    nodes:
      | Node<ReactFlowNodeData>[]
      | ((nodes: Node<ReactFlowNodeData>[]) => Node<ReactFlowNodeData>[])
  ) => void
) {
  setNodes((nodes: Node<ReactFlowNodeData>[]) => updateZIndexOptimized(nodes, nodeId))
}
